import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      navigate(u.role === "admin" ? "/admin" : u.role === "artist" ? "/artist" : "/buyer");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/buyer";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative grain">
        <img src="https://images.unsplash.com/photo-1633887091273-a3bd71efddde?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Artist studio" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[var(--bg-dark)]/50" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="font-serif text-4xl leading-tight">Every acquisition, paid to the artist on delivery.</div>
          <p className="mt-3 text-stone-300 max-w-md">Funds are held securely until your artwork is delivered and confirmed.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="font-serif text-3xl">ARTFUTUR<span className="text-[var(--gold)]">.</span></Link>
          <h1 className="font-serif text-3xl mt-8">Sign in</h1>
          <p className="text-[var(--text-muted)] mt-1 text-sm">Access your collection, sales and orders.</p>

          <button onClick={googleLogin} data-testid="google-login-btn"
            className="mt-8 w-full flex items-center justify-center gap-3 border border-[var(--border-subtle)] rounded-full py-3 text-sm font-medium hover:border-[var(--gold)] transition-colors">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
          </button>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-[var(--border-subtle)]" />
            <span className="text-xs text-[var(--text-muted)] font-mono">OR</span>
            <div className="flex-1 h-px bg-[var(--border-subtle)]" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 w-full rounded-lg border border-[var(--border-subtle)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--gold)]" />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 w-full rounded-lg border border-[var(--border-subtle)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--gold)]" />
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="btn-gold w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
            </button>
          </form>

          <p className="text-sm text-[var(--text-muted)] mt-6 text-center">
            New to ARTFUTUR? <Link to="/register" className="text-[var(--gold)] font-medium">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
